// Порт TDLib match_urls / fix_url / is_email_address (td/telegram/MessageEntity.cpp).
// Перенесён из docs/prototypes/telegram-preview/src/url.ts без изменения алгоритма:
// 227 из 227 золотых векторов TDLib зелёные (см. packages/contracts/test/vectors/).
// Таблица TLD — 1448 записей, из них 151 не-ASCII: наивная выборка [a-z0-9-]* даёт 1297
// и молча теряет весь кириллический сегмент, после чего почта.рф перестаёт быть ссылкой.

import { category } from './match.ts';
import TLDS from './tlds.generated.json' with { type: 'json' };

const tldSet = new Set(TLDS as string[]);

/** TDLib is_common_tld: a TLD whose only difference from lowercase is its first
 *  character is treated as a sentence start, not a TLD ("telegram.Org" is not a URL). */
export function isCommonTld(tld: string): boolean {
  if (/^[a-z]*$/.test(tld)) return tldSet.has(tld);
  const lower = tld.toLowerCase();
  if (lower !== tld && [...lower].slice(1).join('') === [...tld].slice(1).join('')) return false;
  return tldSet.has(lower);
}
/** Индексация строки под noUncheckedIndexedAccess. Алгоритм ниже всегда индексирует
 *  в границах — проверено 227 золотыми векторами TDLib; помощник снимает `| undefined`,
 *  не меняя поведения. */
const cu = (s: string, i: number): string => s[i] as string;
const isDigit = (ch: string) => ch >= '0' && ch <= '9';
const isAlpha = (ch: string) => /^[a-zA-Z]$/.test(ch);
const isAlphaDigit = (ch: string) => /^[0-9a-zA-Z]$/.test(ch);
const isAlphaDigitOrUnderscoreOrMinus = (ch: string) => /^[0-9a-zA-Z_-]$/.test(ch);
const isAlphaDigitOrUnderscoreOrMinusStrict = isAlphaDigitOrUnderscoreOrMinus;

const isUrlUnicodeSymbol = (ch: string) => {
  const c = ch.codePointAt(0) as number;
  if (c >= 0x2000 && c <= 0x206f)
    return c === 0x200c || c === 0x200d || (c >= 0x2010 && c <= 0x2015);
  return category(ch) !== 'Separator';
};
const isUrlPathSymbol = (ch: string) => {
  const c = ch.codePointAt(0) as number;
  if (c === 0x0a || ch === '<' || ch === '>' || ch === '"' || c === 0xab || c === 0xbb)
    return false;
  return isUrlUnicodeSymbol(ch);
};
const isProtocolSymbol = (ch: string) => {
  const c = ch.codePointAt(0) as number;
  if (c < 0x80) return isAlphaDigit(ch) || ch === '+' || ch === '-';
  return category(ch) !== 'Separator';
};
const isUserDataSymbol = (ch: string) => {
  const c = ch.codePointAt(0) as number;
  if ('\n/[]{}()\'`<>"@'.includes(ch)) return false;
  if (c === 0xab || c === 0xbb) return false;
  return isUrlUnicodeSymbol(ch);
};
const isDomainSymbol = (ch: string) => {
  const c = ch.codePointAt(0) as number;
  if (c < 0xc0) return ch === '.' || isAlphaDigitOrUnderscoreOrMinus(ch) || ch === '~';
  return isUrlUnicodeSymbol(ch);
};

const BAD_PATH_END = ".:;,('?!`";

// codepoint-aware cursor helpers over UTF-16 string
const cpAt = (s: string, i: number) => String.fromCodePoint(s.codePointAt(i) as number);
const prevStart = (s: string, i: number) => {
  const c = s.charCodeAt(i - 1);
  return c >= 0xdc00 && c <= 0xdfff && i >= 2 ? i - 2 : i - 1;
};

export type Match = { offset: number; length: number };

export function matchUrls(s: string): Match[] {
  const out: Match[] = [];
  let begin = 0; // window start; backward scans never cross it
  let pos = 0;
  while (true) {
    const dot = s.indexOf('.', pos);
    if (dot < 0 || dot + 1 === s.length) break;
    if (s[dot + 1] === ' ') {
      pos = dot + 2;
      begin = pos;
      continue;
    }

    let domainBegin = dot;
    while (domainBegin !== begin) {
      const p = prevStart(s, domainBegin);
      if (!isDomainSymbol(cpAt(s, p))) break;
      domainBegin = p;
    }

    let lastAt = -1;
    let domainEnd = dot;
    while (domainEnd !== s.length) {
      const ch = cpAt(s, domainEnd);
      if (ch === '@') lastAt = domainEnd;
      else if (!isDomainSymbol(ch)) break;
      domainEnd += ch.length;
    }

    if (lastAt >= 0) {
      while (domainBegin !== begin) {
        const p = prevStart(s, domainBegin);
        if (!isUserDataSymbol(cpAt(s, p))) break;
        domainBegin = p;
      }
    }

    let urlEnd = domainEnd;
    if (urlEnd !== s.length && s[urlEnd] === ':') {
      let portEnd = urlEnd + 1;
      while (portEnd !== s.length && isDigit(cu(s, portEnd))) portEnd++;
      let portBegin = urlEnd + 1;
      while (portBegin !== portEnd && s[portBegin] === '0') portBegin++;
      if (
        portBegin !== portEnd &&
        portEnd - portBegin <= 5 &&
        Number(s.slice(portBegin, portEnd)) <= 65535
      ) {
        urlEnd = portEnd;
      }
    }

    if (urlEnd !== s.length && (s[urlEnd] === '/' || s[urlEnd] === '?' || s[urlEnd] === '#')) {
      let pathEnd = urlEnd + 1;
      while (pathEnd !== s.length) {
        const ch = cpAt(s, pathEnd);
        if (!isUrlPathSymbol(ch)) break;
        pathEnd += ch.length;
      }
      while (pathEnd > urlEnd + 1 && BAD_PATH_END.includes(cu(s, pathEnd - 1))) pathEnd--;
      if (s[urlEnd] === '/' || pathEnd > urlEnd + 1) urlEnd = pathEnd;
    }
    while (urlEnd > dot + 1 && s[urlEnd - 1] === '.') urlEnd--;

    let isBad = false;
    let urlBegin = domainBegin;
    if (urlBegin !== begin && s[urlBegin - 1] === '@') {
      if (lastAt >= 0) isBad = true;
      let userBegin = urlBegin - 1;
      while (userBegin !== begin) {
        const p = prevStart(s, userBegin);
        if (!isUserDataSymbol(cpAt(s, p))) break;
        userBegin = p;
      }
      if (userBegin === urlBegin - 1) isBad = true;
      urlBegin = userBegin;
    }

    if (urlBegin !== begin) {
      const prefix = s.slice(begin, urlBegin);
      if (prefix.length >= 6 && prefix.endsWith('://')) {
        let protoBegin = urlBegin - 3;
        while (protoBegin !== begin) {
          const p = prevStart(s, protoBegin);
          if (!isProtocolSymbol(cpAt(s, p))) break;
          protoBegin = p;
        }
        const protocol = s.slice(protoBegin, urlBegin - 3).toLowerCase();
        if (protocol.endsWith('http') && protocol !== 'shttp') urlBegin -= 7;
        else if (protocol.endsWith('https')) urlBegin -= 8;
        else if (protocol.endsWith('ftp') && protocol !== 'tftp' && protocol !== 'sftp')
          urlBegin -= 6;
        else if (protocol.endsWith('tonsite')) urlBegin -= 10;
        else isBad = true;
      } else {
        const p = prevStart(s, urlBegin);
        const ch = cpAt(s, p);
        const cat = category(ch);
        const isWord =
          cat === 'Letter' || cat === 'DecimalNumber' || cat === 'Number' || ch === '_';
        if (isWord || ch === '/' || ch === '#' || ch === '@') isBad = true;
      }
    }

    if (!isBad) {
      if (urlEnd > dot + 1) out.push({ offset: urlBegin, length: urlEnd - urlBegin });
      while (urlEnd !== s.length && s[urlEnd] === '.') urlEnd++;
    } else {
      while (s[urlEnd - 1] !== '.') urlEnd--;
    }
    if (urlEnd <= dot) urlEnd = dot + 1;
    pos = urlEnd;
    begin = urlEnd;
  }
  return out;
}

export function isEmailAddress(str: string): boolean {
  const at = str.indexOf('@');
  if (at < 0) return false;
  const userdata = str.slice(0, at);
  const domain = str.slice(at + 1);
  if (domain.length === 0) return false;

  let prev = 0,
    parts = 0;
  for (let i = 0; i < userdata.length; i++) {
    const c = userdata[i];
    if (c === '.' || c === '+') {
      if (i - prev >= 27) return false;
      parts++;
      prev = i + 1;
    } else if (!isAlphaDigitOrUnderscoreOrMinusStrict(c as string)) return false;
  }
  parts++;
  if (parts >= 12) return false;
  const last = userdata.length - prev;
  if (last === 0 || last >= 36) return false;

  const dp = domain.split('.');
  if (dp.length <= 1 || dp.length > 7) return false;
  const tld = dp[dp.length - 1] as string;
  if (tld.length <= 1 || tld.length >= 9) return false;
  for (const c of tld) if (!isAlpha(c)) return false;
  for (const part of dp.slice(0, -1)) {
    if (part.length === 0 || part.length >= 31) return false;
    for (const c of part) if (!isAlphaDigitOrUnderscoreOrMinusStrict(c)) return false;
    if (!isAlphaDigit(cu(part, 0)) || !isAlphaDigit(cu(part, part.length - 1))) return false;
  }
  return true;
}

/** returns trimmed url or null */
export function fixUrl(str: string): string | null {
  let full = str;
  let hasProtocol = false;
  const head = str.slice(0, 10).toLowerCase();
  if (
    head.startsWith('http://') ||
    head.startsWith('https://') ||
    head.startsWith('ftp://') ||
    head.startsWith('tonsite://')
  ) {
    str = str.slice(str.indexOf(':') + 3);
    hasProtocol = true;
  }
  const idx = (c: string) => {
    const i = str.indexOf(c);
    return i < 0 ? str.length : i;
  };
  const domainEnd = Math.min(str.length, idx('/'), idx('?'), idx('#'));
  let domain = str.slice(0, domainEnd);
  const path = str.slice(domainEnd);

  const at = domain.indexOf('@');
  if (at >= 0) domain = domain.slice(at + 1);
  const colon = domain.lastIndexOf(':');
  if (colon >= 0) domain = domain.slice(0, colon);

  if (domain.length === 12 && domain.toLowerCase() === 'teiegram.org') return null;

  const bal = [0, 0, 0];
  let pathPos = 0;
  for (; pathPos < path.length; pathPos++) {
    const c = cu(path, pathPos);
    if (c === '(') bal[0] = (bal[0] as number) + 1;
    else if (c === ')') bal[0] = (bal[0] as number) - 1;
    else if (c === '[') bal[1] = (bal[1] as number) + 1;
    else if (c === ']') bal[1] = (bal[1] as number) - 1;
    else if (c === '{') bal[2] = (bal[2] as number) + 1;
    else if (c === '}') bal[2] = (bal[2] as number) - 1;
    if ((bal[0] as number) < 0 || (bal[1] as number) < 0 || (bal[2] as number) < 0) break;
  }
  while (pathPos > 0 && BAD_PATH_END.includes(cu(path, pathPos - 1))) pathPos--;
  full = full.slice(0, full.length - (path.length - pathPos));

  let prev = 0,
    partCount = 0,
    hasNonDigit = false,
    isIpv4 = true;
  for (let i = 0; i <= domain.length; i++) {
    if (i === domain.length || domain[i] === '.') {
      const size = i - prev;
      if (size === 0 || size >= 64 || cu(domain, i - 1) === '-') return null;
      if (isIpv4) {
        if (size > 3) isIpv4 = false;
        if (
          size === 3 &&
          (cu(domain, prev) >= '3' ||
            (cu(domain, prev) === '2' &&
              (cu(domain, prev + 1) >= '6' ||
                (cu(domain, prev + 1) === '5' && cu(domain, prev + 2) >= '6'))))
        )
          isIpv4 = false;
        if (cu(domain, prev) === '0' && size >= 2) isIpv4 = false;
      }
      partCount++;
      if (i !== domain.length) prev = i + 1;
    } else if (!isDigit(cu(domain, i))) {
      isIpv4 = false;
      hasNonDigit = true;
    }
  }
  if (partCount === 1) return null;
  if (isIpv4 && partCount === 4) return full;
  if (!hasNonDigit) return null;

  const tld = domain.slice(prev);
  if ([...tld].length <= 1) return null;
  if (tld.startsWith('xn--')) {
    if (tld.length <= 5) return null;
    for (const c of tld.slice(4)) if (!isAlphaDigit(c)) return null;
  } else {
    if (tld.includes('_') || tld.includes('-')) return null;
    if (!hasProtocol && !isCommonTld(tld)) return null;
  }
  let k = prev - 1;
  while (--k >= 0) {
    if (domain[k] === '_') return null;
    if (domain[k] === '.') break;
  }
  return full;
}

export type UrlHit = { offset: number; length: number; type: 'url' | 'email' };

export function findUrls(s: string): UrlHit[] {
  const out: UrlHit[] = [];
  for (const m of matchUrls(s)) {
    const raw = s.slice(m.offset, m.offset + m.length);
    if (isEmailAddress(raw)) {
      out.push({ ...m, type: 'email' });
      continue;
    }
    if (raw.toLowerCase().startsWith('mailto:') && isEmailAddress(raw.slice(7))) {
      out.push({ offset: m.offset + 7, length: m.length - 7, type: 'email' });
      continue;
    }
    const fixed = fixUrl(raw);
    if (fixed) out.push({ offset: m.offset, length: fixed.length, type: 'url' });
  }
  return out;
}
